// @givmo/app-charity — the not-found surface, inside the ONE layout (CPA-WEB40 · shell contract v3).
//
// ⚠ IT ANSWERS MORE THAN A TYPO (OM-12). Three honest states route here: a module this build does
// not serve (a published, unmounted path such as `/books`), a module this organization's class is
// not offered, and an ordinary wrong address. All three are the same fact to a reader — there is
// nothing at this address — so they get the same page. It renders INSIDE the shell (the
// authenticated catch-all), wearing the Organization section: the sidebar of wherever the person
// came from is not knowable, so that is the honest minimum (T6.4). The BLOCKING compliance-dark
// e2e spec pins the `page-not-found` id at the retired `/filing-register*` addresses (and, since
// CCM-MOD1B, a dark build answers it at `/compliance*` too), so the root id stays.
//
// ⚠ ITS ONE CONTROL NAMES THE PERSON'S LANDING, NOT A PLACE (T1.7). "Go to your apps" pointed at
// `/apps`; under D3/D6 that page is the Global Admin's Apps page, so a place-named control there
// would strand a Member. "Back to your apps" runs the same landing computation the Givmo mark runs
// — an in-app entry — and navigates there directly.
//
// Rendered outside the console's providers (a router-level not-found before the authenticated tree
// mounts) it has no landing to compute and falls back to a plain link to the apps page.

import { use } from 'react';
import { Link } from '@tanstack/react-router';
import { FOCUS_RING, cn, testIdProp } from '@givmo/design-system';
import { CharityConsoleContext } from '../console/charity-console-context';
import { APPS_ROUTE } from '../console/console-routes';
import { useGoToLanding, useLandingDestination } from '../router/console-landing';

// ⚠ THE HOUSE FOCUS RING AND A 44px HIT AREA: a text link, but the one control on this page, so it
// carries the same visible outline as every other control and meets the phone tap floor.
const CONTROL_CLASS = cn(
  'inline-flex min-h-44 items-center rounded-sm font-sans text-s14 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700',
  FOCUS_RING,
);

/** The pinned label — the control names the person's landing, never a place. */
export const NOT_FOUND_CONTROL_LABEL = 'Back to your apps';

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <main
      className="flex min-h-[60vh] flex-col items-center justify-center gap-16 p-24 text-center"
      {...testIdProp('page-not-found')}
    >
      <h1 className="font-sans text-s22 font-w700 tracking-givmo text-text-strong">
        We couldn’t find that page
      </h1>
      <p className="max-w-[420px] font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
        The link may be out of date, or the page may have moved.
      </p>
      {children}
    </main>
  );
}

/** Inside the console: the control computes the landing and follows it. */
function LandingControl() {
  const landing = useLandingDestination('in-app');
  const goToLanding = useGoToLanding();
  return (
    <a
      href={landing.href}
      onClick={(event) => {
        if (event.defaultPrevented || event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        goToLanding();
      }}
      className={CONTROL_CLASS}
      {...testIdProp('page-not-found-home')}
    >
      {NOT_FOUND_CONTROL_LABEL}
    </a>
  );
}

export function NotFound() {
  const inConsole = use(CharityConsoleContext) !== null;
  return (
    <Frame>
      {inConsole ? (
        <LandingControl />
      ) : (
        <Link to={APPS_ROUTE} className={CONTROL_CLASS} {...testIdProp('page-not-found-home')}>
          {NOT_FOUND_CONTROL_LABEL}
        </Link>
      )}
    </Frame>
  );
}
